import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Papa from 'papaparse';
import { Box, Chip, Link, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import {
    ReportError, ReportFilters, ReportPagination, ReportState, downloadCsv, formatDateToYYYYMMDD, pageSlice,
} from '../../components/common/ReportParts';

const inputMethodLabel = (method) => (method === 'System' ? 'D2D' : method === 'Direct' ? 'ARS' : method);

const Stat = ({ label, value }) => (
    <Paper variant="outlined" sx={{ flex: '1 1 180px', p: 2 }}>
        <Typography sx={{ fontSize: 28, fontWeight: 600, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
        <Typography variant="body2" color="text.secondary">
            {label}
        </Typography>
    </Paper>
);

// Response-time report. The SPD variant hides the "late" columns and totals.
const ResponseReportView = ({ stateKey, resetType, fetchReport, showLate, filePrefix }) => {
    const dispatch = useDispatch();
    const { data: reduxData, summary, loading: reduxLoading, error: reduxError } = useSelector((state) => state[stateKey] || {});
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [reportData, setReportData] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hasRun, setHasRun] = useState(false);

    // Clear previous results when the page opens/closes or the dates change.
    useEffect(() => {
        setReportData([]);
        setError(null);
        setHasRun(false);
        dispatch({ type: resetType });
        return () => dispatch({ type: resetType });
    }, [dispatch, resetType, startDate, endDate]);

    useEffect(() => {
        if (reduxData && Array.isArray(reduxData) && reduxData.length > 0) {
            setReportData(reduxData);
            setPage(0);
        }
    }, [reduxData]);

    useEffect(() => {
        if (typeof reduxLoading === 'boolean') setIsLoading(reduxLoading);
    }, [reduxLoading]);

    useEffect(() => {
        if (reduxError) setError(reduxError);
    }, [reduxError]);

    const handleGenerateReport = () => {
        if (!startDate || !endDate) {
            setError('Pick both a start and end date.');
            return;
        }
        setIsLoading(true);
        setError(null);
        setHasRun(true);
        dispatch(fetchReport(formatDateToYYYYMMDD(startDate), formatDateToYYYYMMDD(endDate)))
            .then((response) => {
                if (response && response.invoice_data) setReportData(response.invoice_data);
                setIsLoading(false);
            })
            .catch((err) => {
                setError((err && err.message) || "The report couldn't be loaded. Try again.");
                setIsLoading(false);
            });
    };

    const handleExport = () => {
        const exportData = reportData.map((item) => {
            const row = {
                'Invoice #': item.invoice_id,
                'Service Call Date': item.service_call_date,
                'Pickup Address': item.pickup_address,
                'Input Method': inputMethodLabel(item.input_method),
                'Response Time (min)': item.response_time,
            };
            if (showLate) row.Late = item.is_late ? 'Yes' : 'No';
            return row;
        });
        downloadCsv(
            Papa.unparse(exportData),
            `${filePrefix}_${formatDateToYYYYMMDD(startDate)}_${formatDateToYYYYMMDD(endDate)}.csv`
        );
    };

    const s = summary || {};
    const fixed = (n) => (typeof n === 'number' ? n.toFixed(1) : n || 0);

    return (
        <>
            <ReportFilters
                startDate={startDate}
                endDate={endDate}
                onStartChange={setStartDate}
                onEndChange={setEndDate}
                onRun={handleGenerateReport}
                onExport={handleExport}
                running={isLoading}
                canExport={reportData.length > 0}
            />
            <ReportError message={error} />

            {reportData.length > 0 && !isLoading && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                    <Stat label="Invoices" value={s.totalInvoices || reportData.length} />
                    <Stat label="Average response (min)" value={fixed(s.avgResponse)} />
                    {showLate && <Stat label="Late responses" value={s.totalLates || 0} />}
                    {showLate && <Stat label="Late" value={`${fixed(s.percentLate)}%`} />}
                </Box>
            )}

            <ReportState loading={isLoading} hasRun={hasRun} empty={reportData.length === 0} />

            {!isLoading && reportData.length > 0 && (
                <Paper variant="outlined">
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Invoice</TableCell>
                                    <TableCell>Service call date</TableCell>
                                    <TableCell>Pickup address</TableCell>
                                    <TableCell>Input method</TableCell>
                                    <TableCell align="right">Response (min)</TableCell>
                                    {showLate && <TableCell>Late</TableCell>}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {pageSlice(reportData, page, rowsPerPage).map((row, index) => (
                                    <TableRow hover key={row.invoice_id || index}>
                                        <TableCell>
                                            <Link
                                                component="button"
                                                underline="hover"
                                                onClick={() => window.open(`/invoice/${row.invoice_id}`, '_blank')}
                                                sx={{ fontWeight: 600 }}
                                            >
                                                {row.invoice_id}
                                            </Link>
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.service_call_date}</TableCell>
                                        <TableCell>{row.pickup_address}</TableCell>
                                        <TableCell>{inputMethodLabel(row.input_method)}</TableCell>
                                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                            {row.response_time}
                                        </TableCell>
                                        {showLate && (
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    label={row.is_late ? 'Late' : 'On time'}
                                                    color={row.is_late ? 'error' : 'success'}
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <ReportPagination
                        count={reportData.length}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        onPageChange={setPage}
                        onRowsPerPageChange={(value) => {
                            setRowsPerPage(value);
                            setPage(0);
                        }}
                    />
                </Paper>
            )}
        </>
    );
};

export default ResponseReportView;
